import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Lock, Unlock, Send, CheckCircle2, Undo2, FileSpreadsheet, Circle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { inr } from './IsaKpis';

const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const STEPS = [['draft', 'Prepared'], ['submitted', 'Submitted for approval'], ['approved', 'Approved & locked']];

const Stepper = ({ status }) => {
  const at = STEPS.findIndex(s => s[0] === status);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="isa-signoff-steps">
      {STEPS.map(([k, l], i) => (
        <div key={k} className="flex items-center gap-2">
          {i <= at ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
          <span className={`text-xs ${i <= at ? 'text-slate-900 font-semibold' : 'text-slate-400'}`}>{l}</span>
          {i < STEPS.length - 1 && <span className="w-8 h-px bg-slate-200" />}
        </div>
      ))}
    </div>
  );
};

const Frozen = ({ f, hash }) => (
  <Card className="p-4" data-testid="isa-signoff-frozen">
    <h3 className="text-sm font-bold text-slate-900">Payout as submitted</h3>
    <p className="text-[11px] text-slate-500 mb-3">{f.from} → {f.to} · {f.totals.days} day(s) · fingerprint <span className="font-mono">{hash?.slice(0, 12)}</span></p>
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left py-1">Employee</th><th className="text-right">Hot</th><th className="text-right">Share of Hot</th><th className="text-right">Payout</th></tr></thead>
      <tbody>
        {Object.entries(f.people).map(([k, p]) => (
          <tr key={k} className="border-t border-slate-100" data-testid={`isa-signoff-payout-${k}`}>
            <td className="py-1.5 font-medium">{p.name} <span className="text-slate-400">{p.emp_id}</span></td>
            <td className="text-right">{p.hot}</td><td className="text-right">{p.contribution_pct}%</td>
            <td className={`text-right font-bold ${p.share < 0 ? 'text-rose-600' : ''}`}>{inr(p.share)}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-slate-200 font-bold"><td className="py-1.5">Team net</td><td className="text-right">{f.totals.team_hot}</td><td /><td className={`text-right ${f.totals.net < 0 ? 'text-rose-600' : ''}`}>{inr(f.totals.net)}</td></tr>
      </tbody>
    </table>
    <p className="text-[11px] text-slate-500 mt-2">Incentive {inr(f.totals.incentive)} − penalties {inr(f.totals.penalties)} · target met {f.totals.met_days}/{f.totals.days} days</p>
  </Card>
);

export const IsaSignoffTab = ({ month, onChanged }) => {
  const [d, setD] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.get('/inside-sales-audit/signoff', { params: { month } }).then(r => setD(r.data)), [month]);
  useEffect(() => { setD(null); load(); }, [load]);

  const act = async (action, body, ok) => {
    setBusy(action);
    try { const r = await api.post(`/inside-sales-audit/signoff/${month}/${action}`, body); setD(r.data); setText(''); toast.success(ok); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  const download = async () => {
    try {
      const r = await api.get(`/inside-sales-audit/signoff/${month}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `inside-sales-payout-${month}-OFFICIAL.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Download failed'); }
  };

  if (!d) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const needsReason = d.can_approve || d.can_unlock;
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="isa-signoff-tab">
      <div className="lg:col-span-2 space-y-4">
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-bold text-slate-900">{month} payout sign-off</h3>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${d.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : d.status === 'submitted' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-testid="isa-signoff-status">
              {d.status === 'approved' && <Lock className="w-3 h-3" />}{d.status === 'approved' ? 'Locked' : d.status === 'submitted' ? 'Waiting for admin approval' : 'Not submitted'}</span>
          </div>
          <Stepper status={d.status} />
          <ul className="text-xs space-y-1 text-slate-600" data-testid="isa-signoff-checks">
            <li className={d.month_ended ? 'text-emerald-700' : 'text-amber-700'}>{d.month_ended ? '✓ Month has ended' : '• The month has not ended yet — it can be submitted from the 1st of next month'}</li>
            <li className={d.open_alerts ? 'text-amber-700' : 'text-emerald-700'}>{d.open_alerts ? `• ${d.open_alerts} open alert(s) for this month — review them in the Audit tab first` : '✓ No open alerts for this month'}</li>
            {d.frozen?.missing_days?.length > 0 && <li className="text-amber-700">• {d.frozen.missing_days.length} day(s) had no snapshot when submitted and are not paid</li>}
            <li>Once approved, the month is locked: no back-fills, and the official sheet is available to download.</li>
          </ul>
          {(d.can_submit || needsReason) && (
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={2} className="text-xs"
              placeholder={d.can_submit ? 'Note for the approver (optional)' : d.can_unlock ? 'Reason for unlocking (required)' : 'Reason if sending back (required to send back)'}
              data-testid="isa-signoff-text" />
          )}
          <div className="flex gap-2 flex-wrap">
            {d.can_submit && <Button size="sm" disabled={!!busy} onClick={() => act('submit', { note: text }, 'Submitted for approval')} data-testid="isa-signoff-submit"><Send className="w-3.5 h-3.5 mr-1.5" />Submit for approval</Button>}
            {d.can_approve && <Button size="sm" disabled={!!busy} onClick={() => act('approve', {}, 'Approved and locked')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="isa-signoff-approve"><Lock className="w-3.5 h-3.5 mr-1.5" />Approve & lock</Button>}
            {d.can_approve && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('reject', { reason: text }, 'Sent back')} data-testid="isa-signoff-reject"><Undo2 className="w-3.5 h-3.5 mr-1.5" />Send back</Button>}
            {d.can_unlock && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('unlock', { reason: text }, 'Unlocked — needs a fresh sign-off')} data-testid="isa-signoff-unlock"><Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock</Button>}
            {d.status === 'approved' && <Button size="sm" variant="outline" onClick={download} data-testid="isa-signoff-download"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Download official sheet</Button>}
            {d.status === 'submitted' && !d.can_approve && <span className="text-xs text-slate-500">Waiting for an admin to approve.</span>}
          </div>
        </Card>
        {d.frozen && d.status !== 'draft' && <Frozen f={d.frozen} hash={d.hash} />}
      </div>
      <Card className="p-4" data-testid="isa-signoff-history">
        <h3 className="text-sm font-bold text-slate-900 mb-2">History</h3>
        {(d.history || []).length === 0 && <p className="text-xs text-slate-400">Nothing yet.</p>}
        <div className="space-y-2">
          {[...(d.history || [])].reverse().map((h, i) => (
            <div key={i} className="text-xs border-b border-slate-50 pb-1.5">
              <div className="text-slate-800"><b>{h.by}</b> ({h.role}) {h.action} the payout</div>
              {h.reason && <div className="text-slate-500">“{h.reason}”</div>}
              <div className="text-[10px] text-slate-400">{when(h.at)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
